import { LocalStorage } from '@kulee/helper'
import { getBaseHost, STORAGE_KEYS } from '@kulee/tga-constant'
import type BaseModule from './BaseModule'
import { DEFAULT_APP_CODE } from './config/constant'
import logger from './config/logger'
import getRequestInstance from './config/request'
import AdRequestRate from './helper/AdRequestRate'
import deepMerge from './helper/deepMerge'
import type { ModuleType, TGASDKContext, TGASDKInitBaseOptions, TGASDKInitOptions } from './types'
import type { AppConfig, AppConfigAPI } from './types/app'

class TGASDK {
  private static ready = false
  public static context: TGASDKContext = {} as TGASDKContext
  public static DEFAULT_APP_CODE = DEFAULT_APP_CODE
  public static module: ModuleType = {} as ModuleType

  /**
   * SDK初始化
   * @param options
   * @param options.env 环境
   * @param options.appCode 应用编码
   * @param options.appConfig 应用配置
   */
  public static async init(options: TGASDKInitOptions) {
    const { env, appConfig: AppConfigData = {} } = options

    // 获取应用配置
    const data = await this.getAppConfig(options)
    const appConfig: AppConfig = deepMerge(data, AppConfigData)

    // 内容广告限流
    if (appConfig.adxAdConfig.enabled) {
      const contentAdRequestRate = new AdRequestRate('content')
      if (!contentAdRequestRate.run(appConfig.adxAdConfig.rate)) {
        appConfig.adxAdConfig.enabled = false
      }
    }
    // 游戏广告限流
    ;['web', 'android', 'ios'].forEach(platform => {
      const gameAdConfig = appConfig.gameAdConfig[platform]
      if (gameAdConfig && gameAdConfig.enabled) {
        const gameAdRequestRate = new AdRequestRate('game')
        if (!gameAdRequestRate.run(gameAdConfig.rate)) {
          gameAdConfig.enabled = false
        }
      }
    })

    const { appId, lang } = appConfig

    const request = getRequestInstance({
      env,
      appId,
      lang
    })

    this.context.env = env
    this.context.lang = lang
    this.context.request = request
    this.context.appConfig = appConfig

    this.ready = true
    logger.log('TGASDK inited.')
  }

  /**
   * 添加模块构造函数
   * @param ModuleCtor 模块构造函数
   * @returns 模块实例
   */
  public static addModule<T extends BaseModule>(ModuleCtor: new (context: TGASDKContext) => T): T {
    // @ts-ignore
    const clazz: string = ModuleCtor.clazz

    if (Object.prototype.hasOwnProperty.call(this.module, clazz)) {
      return this.module[clazz] as T
    }

    if (!this.ready) {
      throw new Error('Please call "TGASDK.init()" first!')
    }

    const moduleInstance = new ModuleCtor(this.context)
    this.module[clazz] = moduleInstance

    moduleInstance.run()

    return moduleInstance
  }

  /**
   * 检查资源是否准备就绪
   * @param options
   * @param options.env 环境
   * @param options.appCode 应用编码
   * @returns 是否准备就绪
   */
  public static resourceReady(options: TGASDKInitBaseOptions): boolean {
    return this.getAppConfigFromStorage(options.appCode) !== null
  }

  /**
   * 获取应用配置
   * @param options
   * @param options.env 环境
   * @param options.appCode 应用编码
   * @returns 应用配置
   */
  private static async getAppConfig(options: TGASDKInitBaseOptions): Promise<AppConfig> {
    const { env, appCode } = options

    // TODO：后续稳定后需要缓存
    // const appConfigFromStorage = this.getAppConfigFromStorage(appCode)
    // if (appConfigFromStorage) {
    //   return appConfigFromStorage
    // }

    const request = getRequestInstance({
      env
    })
    const { data } = await request.get<AppConfigAPI>(`${getBaseHost(env)}/api/v1/app/config/info/web`, {
      params: {
        appCode
      }
    })
    const ext: any = JSON.parse(data.extInfo || '{}')
    // 处理数据
    const res: AppConfig = {
      ...data,
      page: {
        ...data.page,
        quickAccessList: ext.page?.quickAccessList || []
      },
      feature: {
        ...data.feature,
        uc: {
          ...data.feature.uc,
          ...(ext.feature?.uc || {})
        },
        wallet: {
          enabled: !!ext.feature?.wallet?.enabled,
          display:
            ext.feature?.wallet?.display && ext.feature?.wallet?.display.length > 0
              ? ext.feature?.wallet?.display
              : undefined
        },
        vip: {
          activeVIPEnabled: !!ext.feature?.vip?.activeVIPEnabled,
          premiumVIPEnabled: !!ext.feature?.vip?.premiumVIPEnabled
        },
        langSelect: {
          enabled: !!ext.feature?.langSelect?.enabled
        }
      },
      adxAdConfig: ext.adxAdConfig || {
        enabled: false,
        testEnabled: false
      },
      contentAdConfig: {
        enabled: false,
        testEnabled: false
      },
      gameAdConfig: ext.gameAdConfig || {
        web: { enabled: false, testEnabled: false },
        android: { enabled: false, testEnabled: false },
        ios: { enabled: false, testEnabled: false }
      }
    }
    LocalStorage.setItemExpire(this.getAppConfigLSKey(appCode), res, 24 * 60 * 60)

    return res
  }

  private static getAppConfigFromStorage(appCode: string): AppConfig | null {
    const appConfigFromStorage = LocalStorage.getItemExpire<AppConfig>(this.getAppConfigLSKey(appCode))
    return appConfigFromStorage || null
  }

  private static getAppConfigLSKey(appCode: string): string {
    return STORAGE_KEYS.APP_CONFIG + '-' + appCode.toLocaleLowerCase()
  }
}

export default TGASDK
